import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { api } from "../lib/api";
import { apiError } from "../lib/errors";
import { toast } from "sonner";
import { X } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const STORAGE_KEY = "mbg_welcome_popup";
const SEVEN_DAYS = 7 * 24 * 60 * 60 * 1000;
// Don't interrupt visitors who are already buying, reserving, or signing in.
const QUIET_PATHS = ["/checkout", "/cart", "/cars/", "/login", "/register", "/reset", "/quote"];

/**
 * First-time visitor welcome popup with email capture.
 * - Shows only if not dismissed/signed-up, after a short delay.
 * - Dismissal is remembered for 7 days; signup is remembered permanently.
 * - Hidden entirely for logged-in users.
 */
export default function WelcomePopup() {
    const { user } = useAuth();
    const { pathname } = useLocation();
    const quiet = QUIET_PATHS.some((p) => pathname.startsWith(p));
    const [open, setOpen] = useState(false);
    const [email, setEmail] = useState("");
    const [done, setDone] = useState(false);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (user || quiet) return;
        let state = null;
        try { state = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null"); } catch { state = null; }
        if (state?.signedUp) return;
        if (state?.dismissedAt && Date.now() - state.dismissedAt < SEVEN_DAYS) return;
        const t = setTimeout(() => setOpen(true), 2500);
        return () => clearTimeout(t);
    }, [user, quiet]);

    const dismiss = () => {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ dismissedAt: Date.now() }));
        setOpen(false);
    };

    useEffect(() => {
        if (!open) return;
        const onKey = (e) => { if (e.key === "Escape") dismiss(); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await api.post("/welcome-signup", { email, source: "welcome_popup" });
            localStorage.setItem(STORAGE_KEY, JSON.stringify({ signedUp: true }));
            setDone(true);
            toast.success("Welcome to MBG.");
            setTimeout(() => setOpen(false), 6000);
        } catch (err) {
            toast.error(apiError(err, "Could not sign up. Please try again."));
        }
        setBusy(false);
    };

    if (!open || quiet) return null;

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="welcome-popup" role="dialog" aria-modal="true" aria-labelledby="welcome-title">
            <div className="absolute inset-0 bg-[#1A1A1A]/60 backdrop-blur-sm" onClick={dismiss} />
            <div className="relative w-full max-w-3xl bg-[#F7F3EB] border border-[#E2D4C1] shadow-[0_40px_120px_-30px_rgba(0,0,0,0.6)] rounded-md overflow-hidden grid md:grid-cols-2 animate-fade-up">
                <button data-testid="welcome-close" onClick={dismiss} aria-label="Close" className="absolute top-3 right-3 z-10 w-9 h-9 grid place-items-center rounded-full bg-[#F7F3EB]/90 border border-[#E2D4C1] text-[#33302C] hover:text-gold transition-colors">
                    <X size={16} aria-hidden="true" />
                </button>
                <div className="hidden md:block relative">
                    <img src="https://images.unsplash.com/photo-1552519507-da3b142c6e3d?w=1000&q=80" alt="" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A]/30 to-transparent" />
                </div>
                <div className="p-8 md:p-10">
                    {done ? (
                        <div className="h-full flex flex-col justify-center text-center py-8" data-testid="welcome-success">
                            <div id="welcome-title" className="font-display text-3xl text-gold mb-3">Welcome to MBG.</div>
                            <p className="text-[#33302C] mb-5">Your welcome kit request has been received.</p>
                            <div className="border border-gold/50 bg-[#8C5A3C]/5 p-5" data-testid="welcome-check-inbox">
                                <div className="text-[10px] tracking-[0.3em] uppercase text-[#6B645C] mb-1">Check your inbox</div>
                                <div className="text-sm text-[#33302C]">We've emailed your first-order discount code (10% off, valid 30 days) to <strong>{email}</strong>.</div>
                            </div>
                        </div>
                    ) : (
                        <>
                            <div className="text-[11px] tracking-[0.4em] uppercase text-gold mb-3">New Customer?</div>
                            <h2 id="welcome-title" className="font-display text-3xl md:text-4xl tracking-tight mb-4">Get Your MBG Welcome Kit</h2>
                            <p className="text-[#33302C] text-sm leading-relaxed mb-6">
                                Get started with MBG Auto Sales. Receive product updates, tint specials, dealer pricing info,
                                installation support, and exclusive welcome offers.
                            </p>
                            <form onSubmit={submit} className="space-y-3" data-testid="welcome-form">
                                <input
                                    required type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                                    placeholder="Email address" aria-label="Email address" autoComplete="email" data-testid="welcome-email"
                                    className="w-full bg-white border border-[#E2D4C1] rounded-none px-4 py-3 text-sm focus:outline-none focus:border-gold"
                                />
                                <button type="submit" disabled={busy} data-testid="welcome-submit" className="w-full bg-gold hover:bg-gold-hover text-white uppercase tracking-[0.25em] text-xs font-semibold py-3.5 transition-colors disabled:opacity-50">
                                    {busy ? "Signing you up…" : "Sign Me Up"}
                                </button>
                                <button type="button" onClick={dismiss} data-testid="welcome-no-thanks" className="w-full text-[11px] tracking-[0.25em] uppercase text-[#6B645C] hover:text-[#1A1A1A] py-1">
                                    No, Thanks
                                </button>
                            </form>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
